import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import { FunctionalRootState, GetHomeParams } from '@share/utils';
import { ILoginState, IMenuState } from '@share/store/slices';
import { Routes } from '@share/constants';
import { IAccount } from '@share/common-types';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faClock, faComments, faEnvelope, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { faFacebook, faInstagram, faYoutube, faPinterest } from '@fortawesome/free-brands-svg-icons';

import TripsByHiltonLogo from '@share/assets/images/profiles/trips-by-hilton.png';

import './style.scss';

export function FooterHilton() {
  const intl = useIntl();
  const [email, setEmail] = useState('');

  const loginStore: ILoginState = useSelector((state: FunctionalRootState) => state.loginStore);
  const menuStore: IMenuState = useSelector((state: FunctionalRootState) => state.navigationMenuStore);

  const { account } = loginStore;
  const { items } = menuStore;

  const accountName = account?.name;
  const homeParams = GetHomeParams(account as IAccount);
  const to = (route: string) => `/${accountName}${route}${homeParams}`;

  const phone = items?.phone;

  const planYourTrip = [
    { title: 'hilton.exp.condos', default: 'Condos', route: Routes.Search },
    { title: 'hilton.exp.cruises', default: 'Cruises', route: Routes.Cruises },
    { title: 'hilton.exp.getaways', default: 'Getaways', route: Routes.Getaways },
    { title: 'hilton.exp.hotels', default: 'Hotels', route: Routes.Search },
    { title: 'hilton.exp.allInclusives', default: 'All Inclusives', route: Routes.AllInclusive },
    { title: 'hilton.exp.homes', default: 'Homes', route: Routes.Homes },
    { title: 'hilton.exp.flights', default: 'Flights', route: Routes.Flights },
  ];

  const memberResources = [
    { title: 'hilton.quick.dailyDeals', default: 'Daily Deals', route: Routes.DailyBenefits },
    { title: 'hilton.quick.beforeYouGo', default: 'Before You Go', route: Routes.DailyBenefits },
    { title: 'hilton.quick.renew', default: 'I Need to Renew My Membership', route: Routes.NewMembers },
    { title: 'hilton.quick.americanLifestyle', default: 'American Lifestyle', route: Routes.DailyBenefits },
    { title: 'hilton.quick.memberVideos', default: 'Member Videos', route: Routes.MemberVideos },
    { title: 'hilton.quick.masterClasses', default: 'Master Classes', route: Routes.Webinars },
  ];

  const aboutUs = [
    { title: 'hilton.footer.about.why', default: 'Why Travel With Us', route: Routes.NewMembers },
    { title: 'hilton.concierge.award.title', default: 'Award-Winning Service You Can Trust', route: Routes.ConciergeTeam },
    { title: 'hilton.partners.title', default: 'Trusted by World-Class Partners', route: Routes.DailyBenefits },
  ];

  const support = [
    { title: 'hilton.footer.support.contact', default: 'Contact Us', route: Routes.ConciergeTeam },
    { title: 'hilton.footer.support.faqs', default: 'FAQs', route: Routes.DailyBenefits },
    { title: 'hilton.footer.support.terms', default: 'Terms & Conditions', route: Routes.DailyBenefits },
  ];

  const renderColumn = (
    titleId: string,
    titleDefault: string,
    links: { title: string; default: string; route: string }[],
  ) => (
    <div className="footer-hilton__col">
      <p className="footer-hilton__col-title">
        <FormattedMessage id={titleId} defaultMessage={titleDefault} />
      </p>
      {links.map((l) => (
        <Link to={to(l.route)} className="footer-hilton__col-link" key={l.title}>
          <FormattedMessage id={l.title} defaultMessage={l.default} />
        </Link>
      ))}
    </div>
  );

  return (
    <div className="footer-hilton">
      {/* Stay Inspired signup band */}
      <div className="footer-hilton__signup">
        <div className="footer-hilton__container footer-hilton__signup-inner">
          <div className="footer-hilton__signup-heading">
            <span className="footer-hilton__signup-icon">
              <FontAwesomeIcon icon={faEnvelope} />
            </span>
            <div className="footer-hilton__signup-text">
            <p className="footer-hilton__signup-title">
              <FormattedMessage id="hilton.footer.signup.title" defaultMessage="Stay Inspired" />
            </p>
            <p className="footer-hilton__signup-copy">
              <FormattedMessage
                id="hilton.footer.signup.copy"
                defaultMessage="Get exclusive deals, travel tips & updates delivered to your inbox."
              />
            </p>
            </div>
          </div>
          <form
            className="footer-hilton__signup-form"
            onSubmit={(e) => e.preventDefault()}
          >
            <div className="footer-hilton__signup-field">
              <input
                type="email"
                className="footer-hilton__signup-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={intl.formatMessage({
                  id: 'hilton.footer.signup.placeholder',
                  defaultMessage: 'Enter your email',
                })}
                aria-label={intl.formatMessage({
                  id: 'hilton.footer.signup.placeholder',
                  defaultMessage: 'Enter your email',
                })}
              />
              <FontAwesomeIcon icon={faChevronDown} className="footer-hilton__signup-chevron" />
            </div>
            <button type="submit" className="footer-hilton__signup-btn">
              <FormattedMessage id="hilton.footer.signup.button" defaultMessage="Sign Me Up" />
            </button>
          </form>
          <div className="footer-hilton__social">
            <span className="footer-hilton__social-label">
              <FormattedMessage id="hilton.footer.followUs" defaultMessage="Follow Us" />
            </span>
            <div className="footer-hilton__social-icons">
              <FontAwesomeIcon icon={faFacebook} />
              <FontAwesomeIcon icon={faInstagram} />
              <FontAwesomeIcon icon={faYoutube} />
              <FontAwesomeIcon icon={faPinterest} />
            </div>
          </div>
        </div>
      </div>

      {/* Link columns */}
      <div className="footer-hilton__container footer-hilton__main">
        <div className="footer-hilton__brand">
          <img
            src={TripsByHiltonLogo}
            alt="Trips by Hilton"
            className="footer-hilton__brand-logo"
          />
        </div>

        {renderColumn('hilton.footer.col.plan', 'Plan Your Trip', planYourTrip)}
        {renderColumn('hilton.footer.col.resources', 'Member Resources', memberResources)}
        {renderColumn('hilton.footer.col.about', 'About Us', aboutUs)}
        {renderColumn('hilton.footer.col.support', 'Support', support)}

        {/* Need Help card */}
        <div className="footer-hilton__help">
          <p className="footer-hilton__help-title">
            <FormattedMessage id="hilton.footer.help.title" defaultMessage="Need Help?" />
          </p>
          {phone ? (
            <a href={`tel:${phone}`} className="footer-hilton__help-phone">
              <FontAwesomeIcon icon={faPhone} /> {phone}
            </a>
          ) : null}
          <p className="footer-hilton__help-hours">
            <FontAwesomeIcon icon={faClock} />{' '}
            <FormattedMessage id="hilton.footer.help.hours" defaultMessage="Mon-Fri 8am-8pm ET" />
          </p>
          <p className="footer-hilton__help-hours">
            <FormattedMessage id="hilton.footer.help.hoursWeekend" defaultMessage="Sat-Sun 9am-6pm ET" />
          </p>
          <Link to={to(Routes.ConciergeTeam)} className="footer-hilton__help-chat">
            <FontAwesomeIcon icon={faComments} style={{ marginRight: '8px' }} />
            <FormattedMessage id="hilton.footer.help.chat" defaultMessage="Chat with Us" />
          </Link>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="footer-hilton__bottom">
        <div className="footer-hilton__container footer-hilton__bottom-inner">
          <span>
            <FormattedMessage
              id="hilton.footer.copyright"
              defaultMessage="© 2025 Hilton Grand Vacations Member Travel. All rights reserved."
            />
          </span>
          <span className="footer-hilton__bottom-secure">
            <FormattedMessage id="hilton.footer.secure" defaultMessage="Secure Site" />
          </span>
        </div>
      </div>
    </div>
  );
}
